/**
 * workflowApi.ts - the Workflows tab's transport (the dataApi.ts pattern).
 *
 * Every call targets `/api/projects/{id}/workflows*` on the project REST
 * surface. The helper adds the bearer token when present and normalises
 * errors to thrown `Error`s carrying the backend `detail`.
 */

import {
  emptyGraphDoc,
  type WorkflowDetail,
  type WorkflowGraphDoc,
  type WorkflowSourceRequest,
  type WorkflowSourceResult,
  type WorkflowStructure,
} from './types';

export type WorkflowOrigin = { origin: string; token?: string; projectId: string };

function headers(token: string | undefined, json = false): Record<string, string> {
  return {
    ...(json ? { 'content-type': 'application/json' } : {}),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  };
}

async function unwrap(r: Response, fallback: string): Promise<any> {
  if (r.ok) return r.json();
  let detail = fallback;
  try {
    const body = await r.json();
    detail = String(body.detail ?? body.message ?? fallback);
  } catch {
    /* keep fallback */
  }
  throw new Error(detail);
}

function base(o: WorkflowOrigin): string {
  return `${o.origin}/api/projects/${encodeURIComponent(o.projectId)}/workflows`;
}

export async function listWorkflows(o: WorkflowOrigin): Promise<WorkflowStructure[]> {
  const data = await unwrap(await fetch(base(o), { headers: headers(o.token) }), 'Loading workflows failed.');
  return Array.isArray(data?.workflows) ? data.workflows : [];
}

export async function scaffoldWorkflow(
  o: WorkflowOrigin,
  slug: string,
  template: string,
): Promise<{ created: string; template: string }> {
  return unwrap(
    await fetch(base(o), {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify({ slug, template }),
    }),
    'Creating the workflow failed.',
  );
}

export async function getGraphDoc(o: WorkflowOrigin, wf: string): Promise<WorkflowGraphDoc> {
  const data = await unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/graph`, { headers: headers(o.token) }),
    `Loading the graph for '${wf}' failed.`,
  );
  return { ...emptyGraphDoc(), ...data };
}

export async function putGraphDoc(o: WorkflowOrigin, wf: string, doc: WorkflowGraphDoc): Promise<void> {
  await unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/graph`, {
      method: 'PUT',
      headers: headers(o.token, true),
      body: JSON.stringify(doc),
    }),
    `Saving the graph for '${wf}' failed.`,
  );
}

// ─── Graph ⇄ code editor ─────────────────────────────────────────────────────

/** Edit-mode init: the derived structure, the authoring statement slices,
 * the cosmetics doc, and whether the workflow round-trips (blockers list the
 * reasons when it doesn't). */
export async function getWorkflowDetail(o: WorkflowOrigin, wf: string): Promise<WorkflowDetail> {
  return unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}`, { headers: headers(o.token) }),
    `Loading '${wf}' failed.`,
  );
}

/** Apply (or dry-run preview) an edited draft graph: the backend regenerates
 * main()'s orchestration from the graph + statement slices. dryRun returns
 * the generated source without writing; a real Apply writes the module and
 * reconciles the cosmetics doc through the id remap. */
export async function putWorkflowSource(
  o: WorkflowOrigin,
  wf: string,
  request: WorkflowSourceRequest,
): Promise<WorkflowSourceResult> {
  return unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/source`, {
      method: 'PUT',
      headers: headers(o.token, true),
      body: JSON.stringify(request),
    }),
    `Generating code for '${wf}' failed.`,
  );
}

// ─── Run control (proxied to the platform's durable engine) ─────────────────

/** A platform workflow run. */
export interface WorkflowRun {
  runId: string;
  workflowName?: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | string;
  input?: unknown[];
  output?: unknown;
  error?: { message?: string; stack?: string } | null;
  eventCount?: number;
  startedAt?: string | null;
  completedAt?: string | null;
  created?: string;
  updated?: string;
  parentRunId?: string | null;
}

/** One event in a run's durable history. */
export interface WorkflowRunEvent {
  eventType: string;
  correlationId?: string;
  payload?: any;
  created?: string;
}

export async function listRuns(o: WorkflowOrigin, wf: string): Promise<WorkflowRun[]> {
  const data = await unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/runs`, { headers: headers(o.token) }),
    `Loading runs for '${wf}' failed.`,
  );
  return Array.isArray(data?.runs) ? data.runs : [];
}

export async function getRunDetail(
  o: WorkflowOrigin,
  wf: string,
  runId: string,
): Promise<{ run: WorkflowRun; events: WorkflowRunEvent[] }> {
  return unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/runs/${encodeURIComponent(runId)}`, {
      headers: headers(o.token),
    }),
    `Loading run '${runId}' failed.`,
  );
}

export async function triggerRun(
  o: WorkflowOrigin,
  wf: string,
  input: unknown[],
): Promise<WorkflowRun> {
  return unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/runs`, {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify({ input }),
    }),
    `Triggering '${wf}' failed.`,
  );
}

/** Fire a workflow from a UI control (⚡ Fire-now / the TriggersPanel):
 * ensure the worker is up (an unregistered workflow — worker never started —
 * is the #1 reason a fire fails) then trigger the run. Worker-start failures
 * are NOT swallowed: the error surfaces with the reason, and the trigger
 * still runs (a worker started elsewhere may already serve it). Returns the
 * started run. */
export async function fireWorkflow(
  o: WorkflowOrigin,
  wf: string,
  input: unknown[],
): Promise<WorkflowRun> {
  const workerErr: string[] = [];
  try {
    await workerStart(o);
  } catch (e) {
    workerErr.push(e instanceof Error ? e.message : String(e));
  }
  try {
    return await triggerRun(o, wf, input);
  } catch (e) {
    const runErr = e instanceof Error ? e.message : String(e);
    throw new Error(
      workerErr.length > 0
        ? `${runErr} (worker failed to start: ${workerErr.join('; ')})`
        : runErr,
    );
  }
}

export async function cancelRun(o: WorkflowOrigin, wf: string, runId: string): Promise<void> {
  await unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/runs/${encodeURIComponent(runId)}`, {
      method: 'DELETE',
      headers: headers(o.token),
    }),
    `Cancelling run '${runId}' failed.`,
  );
}

export async function resumeRun(o: WorkflowOrigin, wf: string, runId: string): Promise<void> {
  await unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/runs/${encodeURIComponent(runId)}/resume`, {
      method: 'POST',
      headers: headers(o.token),
    }),
    `Resuming run '${runId}' failed.`,
  );
}

export async function sendSignal(
  o: WorkflowOrigin,
  wf: string,
  runId: string,
  name: string,
  data: unknown,
): Promise<void> {
  await unwrap(
    await fetch(`${base(o)}/${encodeURIComponent(wf)}/runs/${encodeURIComponent(runId)}/signals`, {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify({ name, data }),
    }),
    `Sending signal '${name}' failed.`,
  );
}

// ─── Worker lifecycle ────────────────────────────────────────────────────────

export interface WorkerStatus {
  running: boolean;
  pid?: number | null;
  exit_code?: number | null;
  started?: boolean;
}

/** The workflow-worker row from the unified tasks poll (`GET /tasks`) - the
 *  truthful running state (start/stop responses go stale across reloads). */
export async function getWorkerStatus(o: WorkflowOrigin): Promise<WorkerStatus | null> {
  const data = await unwrap(
    await fetch(`${o.origin}/api/projects/${encodeURIComponent(o.projectId)}/tasks`, {
      headers: headers(o.token),
    }),
    'Loading tasks failed.',
  );
  const row = (data?.tasks ?? []).find((t: { kind?: string }) => t.kind === 'workflows');
  return row ?? null;
}

export async function workerStart(o: WorkflowOrigin): Promise<WorkerStatus> {
  return unwrap(
    await fetch(`${base(o)}/worker/start`, { method: 'POST', headers: headers(o.token) }),
    'Starting the workflow worker failed.',
  );
}

export async function workerStop(o: WorkflowOrigin): Promise<WorkerStatus> {
  return unwrap(
    await fetch(`${base(o)}/worker/stop`, { method: 'POST', headers: headers(o.token) }),
    'Stopping the workflow worker failed.',
  );
}
